import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import axiosBaseURL from "..";

export const useInputListByCategory = (category: string) => {
  return useQuery({
    queryKey: ["inputs", category],
    staleTime: 12000,
    queryFn: async () => {
      return {
        status: "success",
        message: "message",
        length: 2,
        data: [
          {
            id: 1,
            category: "FERTILIZER",
            name: "DAP",
            unit: "kg",
          },
          {
            id: 2,
            category: "FERTILIZER",
            name: "Urea",
            unit: "kg",
          },
        ],
      };
    },
  });
};

export const useInputList = (options?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "inputs",
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/operation/inputs?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useInputById = (id: string) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["useInputById", id],
    staleTime: 120,
    enabled: !!user?.token && !!id,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/operation/input?id=${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

// export const useInputCategoryList = () => {
//     return useQuery({
//         queryKey: ['categories'],
//         staleTime: 120,
//         queryFn: async () => {
//             const result = await supabase.rpc('admin_get_input_categories');

//             return result;
//         },
//     });
// }

export const useUnits = () => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["unit-of-measurement", "active"],
    staleTime: 60_000,
    enabled: !!user?.token,
    queryFn: async () => {
      const { data } = await axiosBaseURL.get(
        `/admin/unit-of-measurement?activeOnly=true`,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      return {
        status: "success",
        message: "message",
        length: list.length,
        data: list,
      };
    },
  });
};

export const useInputListByContractId = (contractId: string) => {
  return useQuery({
    queryKey: ["input", contractId],
    staleTime: 120,
    queryFn: async () => {
      return {
        status: "success",
        message: "message",
        length: 4,
        data: [
          {
            id: 1,
            category: "FERTILIZER",
            name: "DAP",
            unit: "kg",
          },
          {
            id: 2,
            category: "FERTILIZER",
            name: "Urea",
            unit: "kg",
          },
        ],
      };
    },
  });
};

export const useInputListByOrderId = (orderId: string) => {
  return useQuery({
    queryKey: ["input", orderId],
    staleTime: 120,
    queryFn: async () => {
      return {
        status: "success",
        message: "message",
        length: 4,
        data: [
          {
            id: 1,
            category: "FERTILIZER",
            name: "DAP",
            unit: "kg",
          },
          {
            id: 2,
            category: "FERTILIZER",
            name: "Urea",
            unit: "kg",
          },
          {
            id: 3,
            category: "SEED",
            name: "Potato",
            unit: "kg",
          },
          {
            id: 4,
            category: "SEED",
            name: "Tomato",
            unit: "kg",
          },
        ],
      };
    },
  });
};

export const useCreateInput = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useInputList();

  return useMutation({
    mutationFn: async (input: {
      category: string;
      name: string;
      unitOfMeasurementId: number;
      description: string;
    }) => {
      const result = await axiosBaseURL.post(`/operation/inputs`, input, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
  });
};

export const useUpdateInput = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: number;
      input: {
        category: string;
        name: string;
        unitOfMeasurementId: number;
        description: string;
      };
    }) => {
      await axiosBaseURL.patch(`/operation/inputs/${data.id}`, data.input, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inputs"] });
    },
  });
};

export const useDeleteInput = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useInputList();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await axiosBaseURL.delete(`/operation/inputs/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
  });
};

export const useAddInput = () => {
  return useMutation({
    mutationFn: async (data: { inputId: number; contractId: number }) => {
      const _result = {
        status: "success",
        message: "message",
        data,
      };

      return _result;
    },
  });
};

export const useAddInputToOrder = () => {
  return useMutation({
    mutationFn: async (data: { inputId: number; orderId: number }) => {
      const _result = {
        status: "success",
        message: "message",
        data,
      };

      return _result;
    },
  });
};
